/**
 * W3C Verifiable Credentials Data Model 2.0 — eddsa-jcs-2022 cryptosuite.
 *
 * Implements VC issuance, signing, and verification using:
 * - JSON Canonicalization Scheme (RFC 8785) via existing `canonicalize()`
 * - SHA-256 hashing for proof config + document
 * - Ed25519 detached signatures (libsodium)
 * - Multibase z-base58btc encoding for proofValue
 *
 * @see https://www.w3.org/TR/vc-data-model-2.0/
 * @see https://www.w3.org/TR/vc-di-eddsa/ (eddsa-jcs-2022 cryptosuite)
 */
import sodium from "libsodium-wrappers-sumo";

import { canonicalize, publicKeyToMultibase } from "./did.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** W3C VC 2.0 context — MUST be first element in @context array. */
export const VC_CONTEXT_V2 = "https://www.w3.org/ns/credentials/v2";

/** AgentVault credential vocabulary context. */
export const AV_CREDENTIAL_CONTEXT = "https://agentvault.chat/ns/credentials/v1";

/** Data Integrity v2 context (bundled in credentials/v2 but listed for clarity). */
export const DATA_INTEGRITY_CONTEXT = "https://w3id.org/security/data-integrity/v2";

/** Cryptosuite identifier for JCS-based Ed25519 proofs. */
export const CRYPTOSUITE = "eddsa-jcs-2022";

/** AgentVault issuer DID prefix. */
export const AV_ISSUER_PREFIX = "did:hub:";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** W3C Data Integrity Proof (eddsa-jcs-2022). */
export interface DataIntegrityProof {
  type: "DataIntegrityProof";
  cryptosuite: typeof CRYPTOSUITE;
  created: string;
  verificationMethod: string;
  proofPurpose: "assertionMethod" | "authentication";
  proofValue: string;
}

/** Credential subject — flexible key-value claims. */
export interface CredentialSubject {
  id: string;
  [key: string]: unknown;
}

/** Credential status for revocation checking. */
export interface CredentialStatus {
  id: string;
  type: string;
  statusPurpose?: string;
  statusListIndex?: string;
  statusListCredential?: string;
}

/** W3C Verifiable Credential (unsigned — before proof is attached). */
export interface UnsignedCredential {
  "@context": (string | Record<string, unknown>)[];
  id: string;
  type: string[];
  issuer: string | { id: string; name?: string };
  validFrom: string;
  validUntil?: string;
  name?: string;
  description?: string;
  credentialSubject: CredentialSubject;
  credentialStatus?: CredentialStatus;
  evidence?: Record<string, unknown>[];
}

/** W3C Verifiable Credential (signed — with embedded proof). */
export interface VerifiableCredential extends UnsignedCredential {
  proof: DataIntegrityProof;
}

/** W3C Verifiable Presentation (unsigned). */
export interface UnsignedPresentation {
  "@context": (string | Record<string, unknown>)[];
  type: string | string[];
  id?: string;
  holder: string;
  verifiableCredential: VerifiableCredential[];
}

/** W3C Verifiable Presentation (signed). */
export interface VerifiablePresentation extends UnsignedPresentation {
  proof: DataIntegrityProof;
}

// ---------------------------------------------------------------------------
// AgentVault credential type definitions
// ---------------------------------------------------------------------------

/** Parameters for building an AgentTrustCredential. */
export interface AgentTrustCredentialParams {
  /** Credential UUID (urn:uuid:...) */
  credentialId: string;
  /** Issuer hub DID (did:hub:platform.agentvault.hub or similar) */
  issuerDid: string;
  /** Issuer display name */
  issuerName?: string;
  /** Subject agent DID (did:hub:<hub_address>) */
  subjectDid: string;
  /** Trust tier achieved */
  trustTier: "verified" | "trusted" | "certified" | "premier" | "enterprise";
  /** Composite trust score (0.0 - 1.0) */
  compositeScore: number;
  /** Score window in days */
  scoreWindow: number;
  /** Individual dimension scores */
  dimensions?: {
    volume?: number;
    responsiveness?: number;
    uptime?: number;
    compliance?: number;
  };
  /** ISO 8601 validity start (defaults to now) */
  validFrom?: string;
  /** ISO 8601 validity end */
  validUntil?: string;
  /** Verification API endpoint */
  verificationEndpoint?: string;
}

/** Parameters for building a SkillAttestation credential. */
export interface SkillAttestationParams {
  credentialId: string;
  issuerDid: string;
  issuerName?: string;
  subjectDid: string;
  skillName: string;
  skillNamespace: string;
  /** Skill proficiency level */
  proficiencyLevel?: "basic" | "intermediate" | "advanced" | "expert";
  /** Total invocations of this skill */
  totalInvocations?: number;
  /** Success rate (0.0 - 1.0) */
  successRate?: number;
  /** Assessment date */
  assessedAt?: string;
  validFrom?: string;
  validUntil?: string;
}

/** Parameters for building a PerformanceRecord credential. */
export interface PerformanceRecordParams {
  credentialId: string;
  issuerDid: string;
  issuerName?: string;
  subjectDid: string;
  /** Reporting period start */
  periodStart: string;
  /** Reporting period end */
  periodEnd: string;
  /** Total messages handled */
  messagesHandled: number;
  /** Average response time in milliseconds */
  avgResponseTimeMs: number;
  /** Uptime percentage (0.0 - 1.0) */
  uptimePercentage: number;
  /** Policy violations count */
  policyViolations: number;
  /** Composite score for the period */
  compositeScore: number;
  validFrom?: string;
  validUntil?: string;
}

/** Trust report dimension sub-scores. */
export interface TrustReportDimensions {
  categories: {
    operational: {
      availability?: number | null;
      responsiveness?: number | null;
      reliability?: number | null;
    };
    performance: {
      throughput?: number | null;
      taskSuccess?: number | null;
      efficiency?: number | null;
    };
    quality: {
      toolAccuracy?: number | null;
      errorDiscipline?: number | null;
      compliance?: number | null;
    };
    stability: {
      behavioralConsistency?: number | null;
      versionHealth?: number | null;
    };
    cost: {
      tokenEfficiency?: number | null;
    };
  };
  categoryScores: Record<string, number | null>;
  compositeScore: number;
}

/** Parameters for building an AgentTrustReport credential. */
export interface AgentTrustReportParams {
  credentialId: string;
  issuerDid: string;
  issuerName?: string;
  subjectDid: string;
  /** Report period start (ISO 8601) */
  reportPeriodStart: string;
  /** Report period end (ISO 8601) */
  reportPeriodEnd: string;
  /** Trust tier */
  trustTier: "verified" | "trusted" | "certified" | "premier" | "enterprise";
  /** Composite trust score (0.0 - 1.0) */
  compositeScore: number;
  /** Categorized dimension scores */
  dimensions?: TrustReportDimensions;
  /** Trending analysis */
  trending?: {
    direction: string;
    slope: number;
    r_squared: number;
    pct_change: number;
    data_points: number;
  };
  /** Fleet comparison data */
  fleetComparison?: {
    percentile: number;
    fleet_median: number;
    fleet_p25: number;
    fleet_p75: number;
    rank: number;
    fleet_size: number;
  };
  /** Drift detection status */
  driftStatus?: {
    psi: number;
    classification: string;
    baseline_available: boolean;
  };
  /** Audit chain integrity */
  auditIntegrity?: {
    chain_length: number;
    latest_hash: string | null;
    verified: boolean;
    events_checked: number;
  };
  /** Telemetry summary */
  telemetrySummary?: {
    total_spans: number;
    error_count: number;
    error_rate: number;
    avg_latency_ms: number | null;
    tokens_input: number;
    tokens_output: number;
    latency_p50: number | null;
    latency_p95: number | null;
  };
  validFrom?: string;
  validUntil?: string;
  verificationEndpoint?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * SHA-256 hash using libsodium.
 * libsodium-wrappers-sumo exposes crypto_hash_sha256.
 */
async function sha256(data: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_hash_sha256(data);
}

/**
 * Base58btc encoding (same alphabet as did.ts).
 */
const BASE58_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

function base58Encode(bytes: Uint8Array): string {
  let zeros = 0;
  for (const b of bytes) {
    if (b === 0) zeros++;
    else break;
  }
  let num = BigInt(0);
  for (const b of bytes) {
    num = num * 256n + BigInt(b);
  }
  let result = "";
  while (num > 0n) {
    const remainder = Number(num % 58n);
    num = num / 58n;
    result = BASE58_ALPHABET[remainder] + result;
  }
  return "1".repeat(zeros) + result;
}

function base58Decode(str: string): Uint8Array {
  let zeros = 0;
  for (const c of str) {
    if (c === "1") zeros++;
    else break;
  }
  let num = BigInt(0);
  for (const c of str.slice(zeros)) {
    const idx = BASE58_ALPHABET.indexOf(c);
    if (idx === -1) throw new Error(`Invalid base58 character: ${c}`);
    num = num * 58n + BigInt(idx);
  }
  const hex = num === 0n ? "" : num.toString(16).padStart(2, "0");
  const paddedHex = hex.length % 2 ? "0" + hex : hex;
  const dataBytes = new Uint8Array(
    (paddedHex.match(/.{2}/g) ?? []).map((b) => parseInt(b, 16)),
  );
  const result = new Uint8Array(zeros + dataBytes.length);
  result.set(dataBytes, zeros);
  return result;
}

// ---------------------------------------------------------------------------
// Core VC signing / verification (eddsa-jcs-2022)
// ---------------------------------------------------------------------------

/**
 * Compute the eddsa-jcs-2022 hashData (64 bytes) for signing.
 *
 * Per the spec:
 * 1. JCS canonicalize proof config → SHA-256 → 32 bytes
 * 2. JCS canonicalize document (without proof) → SHA-256 → 32 bytes
 * 3. Concatenate: proofConfigHash || documentHash
 */
async function computeHashData(
  document: Record<string, unknown>,
  proofConfig: Record<string, unknown>,
): Promise<Uint8Array> {
  // Hash the proof configuration
  const proofConfigBytes = canonicalize(proofConfig);
  const proofConfigHash = await sha256(proofConfigBytes);

  // Hash the document (without proof property)
  const docWithoutProof = { ...document };
  delete docWithoutProof.proof;
  const documentBytes = canonicalize(docWithoutProof);
  const documentHash = await sha256(documentBytes);

  // Concatenate: proofConfigHash || documentHash
  const hashData = new Uint8Array(64);
  hashData.set(proofConfigHash, 0);
  hashData.set(documentHash, 32);
  return hashData;
}

/**
 * Sign a Verifiable Credential or Presentation using eddsa-jcs-2022.
 *
 * @param document  The unsigned credential/presentation (any JSON object)
 * @param privateKey  Ed25519 private key (64 bytes — libsodium combined format)
 * @param verificationMethod  DID URL of the signing key (e.g., "did:hub:x#owner-key")
 * @param proofPurpose  "assertionMethod" for credentials, "authentication" for presentations
 * @param created  ISO 8601 timestamp (defaults to now)
 * @returns The signed document with embedded `proof` property
 */
export async function signWithDataIntegrity<
  T extends Record<string, unknown>,
>(
  document: T,
  privateKey: Uint8Array,
  verificationMethod: string,
  proofPurpose: "assertionMethod" | "authentication" = "assertionMethod",
  created?: string,
): Promise<T & { proof: DataIntegrityProof }> {
  await sodium.ready;

  const timestamp = created ?? new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  // Build proof configuration (everything except proofValue)
  const proofConfig: Record<string, unknown> = {
    type: "DataIntegrityProof",
    cryptosuite: CRYPTOSUITE,
    created: timestamp,
    verificationMethod,
    proofPurpose,
  };

  // Compute hashData = SHA-256(proofConfig) || SHA-256(document)
  const hashData = await computeHashData(document as Record<string, unknown>, proofConfig);

  // Sign with Ed25519
  const signature = sodium.crypto_sign_detached(hashData, privateKey);

  // Encode as multibase z-base58btc
  const proofValue = "z" + base58Encode(signature);

  const proof: DataIntegrityProof = {
    type: "DataIntegrityProof",
    cryptosuite: CRYPTOSUITE,
    created: timestamp,
    verificationMethod,
    proofPurpose,
    proofValue,
  };

  return { ...document, proof };
}

/**
 * Verify a Data Integrity proof (eddsa-jcs-2022) on a credential or presentation.
 *
 * @param document  The signed document with embedded `proof`
 * @param publicKey  Ed25519 public key (32 bytes) of the signer
 * @returns true if the proof is valid
 */
export async function verifyDataIntegrity(
  document: Record<string, unknown>,
  publicKey: Uint8Array,
): Promise<boolean> {
  await sodium.ready;

  const proof = document.proof as DataIntegrityProof | undefined;
  if (!proof || proof.type !== "DataIntegrityProof" || proof.cryptosuite !== CRYPTOSUITE) {
    return false;
  }

  // Decode proofValue from multibase z-base58btc
  if (!proof.proofValue.startsWith("z")) {
    return false;
  }
  const signature = base58Decode(proof.proofValue.slice(1));
  if (signature.length !== 64) {
    return false;
  }

  // Rebuild proof configuration (without proofValue)
  const proofConfig: Record<string, unknown> = {
    type: proof.type,
    cryptosuite: proof.cryptosuite,
    created: proof.created,
    verificationMethod: proof.verificationMethod,
    proofPurpose: proof.proofPurpose,
  };

  // Recompute hashData
  const hashData = await computeHashData(document, proofConfig);

  // Verify Ed25519 signature
  try {
    return sodium.crypto_sign_verify_detached(signature, hashData, publicKey);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Credential builders — AgentVault-specific credential types
// ---------------------------------------------------------------------------

/**
 * Build an unsigned AgentTrustCredential.
 *
 * Issued when an agent achieves or maintains a trust tier.
 */
export function buildAgentTrustCredential(
  params: AgentTrustCredentialParams,
): UnsignedCredential {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  return {
    "@context": [
      VC_CONTEXT_V2,
      {
        AgentTrustCredential: "https://agentvault.chat/vocab#AgentTrustCredential",
        trustTier: "https://agentvault.chat/vocab#trustTier",
        compositeScore: "https://agentvault.chat/vocab#compositeScore",
        scoreWindow: "https://agentvault.chat/vocab#scoreWindow",
        scoreDimensions: "https://agentvault.chat/vocab#scoreDimensions",
        volume: "https://agentvault.chat/vocab#volume",
        responsiveness: "https://agentvault.chat/vocab#responsiveness",
        uptime: "https://agentvault.chat/vocab#uptime",
        compliance: "https://agentvault.chat/vocab#compliance",
        verificationEndpoint: "https://agentvault.chat/vocab#verificationEndpoint",
      },
    ],
    id: params.credentialId,
    type: ["VerifiableCredential", "AgentTrustCredential"],
    issuer: params.issuerName
      ? { id: params.issuerDid, name: params.issuerName }
      : params.issuerDid,
    validFrom: params.validFrom ?? now,
    ...(params.validUntil ? { validUntil: params.validUntil } : {}),
    credentialSubject: {
      id: params.subjectDid,
      trustTier: params.trustTier,
      compositeScore: params.compositeScore,
      scoreWindow: params.scoreWindow,
      ...(params.dimensions
        ? {
            scoreDimensions: {
              volume: params.dimensions.volume,
              responsiveness: params.dimensions.responsiveness,
              uptime: params.dimensions.uptime,
              compliance: params.dimensions.compliance,
            },
          }
        : {}),
      ...(params.verificationEndpoint
        ? { verificationEndpoint: params.verificationEndpoint }
        : {}),
    },
  };
}

/**
 * Build an unsigned SkillAttestation credential.
 *
 * Issued for verified agent capabilities/skills.
 */
export function buildSkillAttestation(
  params: SkillAttestationParams,
): UnsignedCredential {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  return {
    "@context": [
      VC_CONTEXT_V2,
      {
        SkillAttestation: "https://agentvault.chat/vocab#SkillAttestation",
        skillName: "https://agentvault.chat/vocab#skillName",
        skillNamespace: "https://agentvault.chat/vocab#skillNamespace",
        proficiencyLevel: "https://agentvault.chat/vocab#proficiencyLevel",
        totalInvocations: "https://agentvault.chat/vocab#totalInvocations",
        successRate: "https://agentvault.chat/vocab#successRate",
        assessedAt: "https://agentvault.chat/vocab#assessedAt",
      },
    ],
    id: params.credentialId,
    type: ["VerifiableCredential", "SkillAttestation"],
    issuer: params.issuerName
      ? { id: params.issuerDid, name: params.issuerName }
      : params.issuerDid,
    validFrom: params.validFrom ?? now,
    ...(params.validUntil ? { validUntil: params.validUntil } : {}),
    credentialSubject: {
      id: params.subjectDid,
      skillName: params.skillName,
      skillNamespace: params.skillNamespace,
      ...(params.proficiencyLevel ? { proficiencyLevel: params.proficiencyLevel } : {}),
      ...(params.totalInvocations !== undefined
        ? { totalInvocations: params.totalInvocations }
        : {}),
      ...(params.successRate !== undefined ? { successRate: params.successRate } : {}),
      ...(params.assessedAt ? { assessedAt: params.assessedAt } : {}),
    },
  };
}

/**
 * Build an unsigned PerformanceRecord credential.
 *
 * Issued based on aggregated agent performance metrics.
 */
export function buildPerformanceRecord(
  params: PerformanceRecordParams,
): UnsignedCredential {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  return {
    "@context": [
      VC_CONTEXT_V2,
      {
        PerformanceRecord: "https://agentvault.chat/vocab#PerformanceRecord",
        periodStart: "https://agentvault.chat/vocab#periodStart",
        periodEnd: "https://agentvault.chat/vocab#periodEnd",
        messagesHandled: "https://agentvault.chat/vocab#messagesHandled",
        avgResponseTimeMs: "https://agentvault.chat/vocab#avgResponseTimeMs",
        uptimePercentage: "https://agentvault.chat/vocab#uptimePercentage",
        policyViolations: "https://agentvault.chat/vocab#policyViolations",
        compositeScore: "https://agentvault.chat/vocab#compositeScore",
      },
    ],
    id: params.credentialId,
    type: ["VerifiableCredential", "PerformanceRecord"],
    issuer: params.issuerName
      ? { id: params.issuerDid, name: params.issuerName }
      : params.issuerDid,
    validFrom: params.validFrom ?? now,
    ...(params.validUntil ? { validUntil: params.validUntil } : {}),
    credentialSubject: {
      id: params.subjectDid,
      periodStart: params.periodStart,
      periodEnd: params.periodEnd,
      messagesHandled: params.messagesHandled,
      avgResponseTimeMs: params.avgResponseTimeMs,
      uptimePercentage: params.uptimePercentage,
      policyViolations: params.policyViolations,
      compositeScore: params.compositeScore,
    },
  };
}

/**
 * Build an unsigned AgentTrustReport credential.
 *
 * Issued as a comprehensive trust report with dimensions, trending,
 * fleet comparison, drift detection, and audit integrity data.
 */
export function buildAgentTrustReportCredential(
  params: AgentTrustReportParams,
): UnsignedCredential {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  const subject: Record<string, unknown> = {
    id: params.subjectDid,
    reportPeriod: {
      start: params.reportPeriodStart,
      end: params.reportPeriodEnd,
    },
    trustTier: params.trustTier,
    compositeScore: params.compositeScore,
  };

  if (params.dimensions) subject.dimensions = params.dimensions;
  if (params.trending) subject.trending = params.trending;
  if (params.fleetComparison) subject.fleetComparison = params.fleetComparison;
  if (params.driftStatus) subject.driftStatus = params.driftStatus;
  if (params.auditIntegrity) subject.auditIntegrity = params.auditIntegrity;
  if (params.telemetrySummary) subject.telemetrySummary = params.telemetrySummary;
  if (params.verificationEndpoint) subject.verificationEndpoint = params.verificationEndpoint;

  return {
    "@context": [
      VC_CONTEXT_V2,
      {
        AgentTrustReport: "https://agentvault.chat/vocab#AgentTrustReport",
        reportPeriod: "https://agentvault.chat/vocab#reportPeriod",
        trustTier: "https://agentvault.chat/vocab#trustTier",
        compositeScore: "https://agentvault.chat/vocab#compositeScore",
        dimensions: "https://agentvault.chat/vocab#dimensions",
        trending: "https://agentvault.chat/vocab#trending",
        fleetComparison: "https://agentvault.chat/vocab#fleetComparison",
        driftStatus: "https://agentvault.chat/vocab#driftStatus",
        auditIntegrity: "https://agentvault.chat/vocab#auditIntegrity",
        telemetrySummary: "https://agentvault.chat/vocab#telemetrySummary",
        verificationEndpoint: "https://agentvault.chat/vocab#verificationEndpoint",
      },
    ],
    id: params.credentialId,
    type: ["VerifiableCredential", "AgentTrustReport"],
    issuer: params.issuerName
      ? { id: params.issuerDid, name: params.issuerName }
      : params.issuerDid,
    validFrom: params.validFrom ?? now,
    ...(params.validUntil ? { validUntil: params.validUntil } : {}),
    credentialSubject: subject as CredentialSubject,
  };
}

/**
 * Build an unsigned Verifiable Presentation wrapping one or more credentials.
 */
export function buildVerifiablePresentation(
  holderDid: string,
  credentials: VerifiableCredential[],
  presentationId?: string,
): UnsignedPresentation {
  return {
    "@context": [VC_CONTEXT_V2],
    type: "VerifiablePresentation",
    ...(presentationId ? { id: presentationId } : {}),
    holder: holderDid,
    verifiableCredential: credentials,
  };
}

// ---------------------------------------------------------------------------
// Convenience wrappers
// ---------------------------------------------------------------------------

/**
 * Issue a signed Verifiable Credential.
 *
 * Convenience wrapper that signs an unsigned credential using eddsa-jcs-2022.
 *
 * @param credential  Unsigned credential to sign
 * @param privateKey  Issuer's Ed25519 private key (64-byte combined format)
 * @param verificationMethod  DID URL of the issuer's signing key
 * @returns Signed VerifiableCredential with embedded proof
 */
export async function issueCredential(
  credential: UnsignedCredential,
  privateKey: Uint8Array,
  verificationMethod: string,
): Promise<VerifiableCredential> {
  return signWithDataIntegrity(
    credential as unknown as Record<string, unknown>,
    privateKey,
    verificationMethod,
    "assertionMethod",
  ) as unknown as Promise<VerifiableCredential>;
}

/**
 * Create a signed Verifiable Presentation.
 *
 * Signs the presentation with the holder's key using "authentication" proof purpose.
 */
export async function presentCredentials(
  presentation: UnsignedPresentation,
  privateKey: Uint8Array,
  verificationMethod: string,
): Promise<VerifiablePresentation> {
  return signWithDataIntegrity(
    presentation as unknown as Record<string, unknown>,
    privateKey,
    verificationMethod,
    "authentication",
  ) as unknown as Promise<VerifiablePresentation>;
}
